import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { vehicleGalleryAPI, vehicleCategoriesAPI } from '../services/api';
import { 
  Plane, Loader2, ArrowLeft, Gauge, Weight, Ruler, 
  Wind, Battery, Clock, Target, Zap, ChevronRight 
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const VehicleCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();

  // Dynamic page title based on category
  useEffect(() => {
    if (category) {
      const categoryName = language === 'tr' && category.name_tr ? category.name_tr : category.name;
      document.title = `${categoryName} | Kartal Air`;
    }
    return () => {
      document.title = 'Kartal Air | SUAS 2026';
    };
  }, [category, language]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch category info
        const categoriesData = await vehicleCategoriesAPI.getAll();
        const currentCategory = categoriesData.find(c => c.id === categoryId);
        
        if (!currentCategory) {
          setError('Category not found');
          setLoading(false);
          return;
        }
        
        setCategory(currentCategory);
        
        // Fetch vehicles for this category
        const vehiclesData = await vehicleGalleryAPI.getAll();
        const filteredVehicles = vehiclesData.filter(v => v.category === categoryId);
        setVehicles(filteredVehicles);
        
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchData();
    }
  }, [categoryId]);

  // Get localized text
  const getLocalizedText = (item, field) => {
    if (language === 'tr' && item[`${field}_tr`]) {
      return item[`${field}_tr`];
    }
    return item[field] || '';
  };

  // Spec labels with translations
  const specLabels = {
    mtow: { en: 'Max Takeoff Weight', tr: 'Maksimum Kalkış Ağırlığı', icon: Weight },
    wingspan: { en: 'Wingspan', tr: 'Kanat Açıklığı', icon: Ruler },
    length: { en: 'Length', tr: 'Uzunluk', icon: Ruler },
    cruise_speed: { en: 'Cruise Speed', tr: 'Seyir Hızı', icon: Gauge },
    max_speed: { en: 'Max Speed', tr: 'Maksimum Hız', icon: Zap },
    endurance: { en: 'Endurance', tr: 'Dayanıklılık', icon: Clock },
    range: { en: 'Range', tr: 'Menzil', icon: Target },
    ceiling: { en: 'Service Ceiling', tr: 'Servis Tavanı', icon: Wind },
    payload: { en: 'Payload Capacity', tr: 'Yük Kapasitesi', icon: Battery },
    propulsion: { en: 'Propulsion', tr: 'İtki Sistemi', icon: Plane }
  };

  const getSpecLabel = (key) => {
    return specLabels[key]?.[language] || specLabels[key]?.en || key;
  };

  const getSpecIcon = (key) => {
    return specLabels[key]?.icon || Gauge;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" style={{ background: '#0F172A' }}>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'tr' ? 'Kategori Bulunamadı' : 'Category Not Found'}
          </h1>
          <p className="text-gray-400 mb-8">
            {language === 'tr' 
              ? 'Aradığınız araç kategorisi mevcut değil.' 
              : 'The vehicle category you are looking for does not exist.'}
          </p>
          <Link 
            to="/aircraft"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
              color: '#0F172A'
            }}
          >
            <ArrowLeft size={20} />
            {language === 'tr' ? 'Tüm Araçlara Dön' : 'Back to All Vehicles'}
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = language === 'tr' && category.name_tr ? category.name_tr : category.name;

  return (
    <div className="min-h-screen" style={{ background: '#0F172A' }}>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-15"
            style={{ background: 'radial-gradient(circle, #0066FF 0%, transparent 70%)' }}
          />
        </div>

        <div className="max-w-[1400px] mx-auto px-[7.6923%] relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-gray-400 hover:text-cyan-400 transition-colors">
              {t('nav.home')}
            </Link>
            <ChevronRight size={16} className="text-gray-600" />
            <Link to="/aircraft" className="text-gray-400 hover:text-cyan-400 transition-colors">
              {t('nav.aircraft')}
            </Link>
            <ChevronRight size={16} className="text-gray-600" />
            <span className="text-cyan-400">{categoryName}</span>
          </nav>

          {/* Category Title */}
          <div className="flex items-center gap-6 mb-8">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.2) 0%, rgba(0, 102, 255, 0.2) 100%)',
                border: '2px solid rgba(0, 240, 255, 0.3)'
              }}
            >
              <Plane size={40} className="text-cyan-400" />
            </div>
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-2"
                style={{
                  background: 'linear-gradient(135deg, #F8FAFC 0%, #94A3B8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {categoryName}
              </h1>
              <p className="text-lg text-gray-400">
                {vehicles.length} {language === 'tr' ? 'araç bu kategoride' : 'vehicles in this category'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles Grid */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          {vehicles.length === 0 ? (
            <div className="text-center py-20">
              <Plane size={64} className="mx-auto text-gray-600 mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-4">
                {language === 'tr' ? 'Henüz Araç Eklenmemiş' : 'No Vehicles Yet'}
              </h3>
              <p className="text-gray-400 mb-8">
                {language === 'tr' 
                  ? 'Bu kategoride henüz araç bulunmuyor.' 
                  : 'There are no vehicles in this category yet.'}
              </p>
              <Link 
                to="/aircraft"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all"
                style={{
                  background: 'rgba(0, 240, 255, 0.1)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#00F0FF'
                }}
              >
                <ArrowLeft size={20} />
                {language === 'tr' ? 'Tüm Araçlara Dön' : 'Back to All Vehicles'}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {vehicles.map((vehicle) => (
                <VehicleCard 
                  key={vehicle.id} 
                  vehicle={vehicle} 
                  language={language}
                  getLocalizedText={getLocalizedText}
                  specLabels={specLabels}
                  getSpecLabel={getSpecLabel}
                  getSpecIcon={getSpecIcon}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Back Button */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] text-center">
          <Link 
            to="/aircraft"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
              color: '#0F172A',
              boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)'
            }}
          >
            <ArrowLeft size={20} />
            {language === 'tr' ? 'Tüm Araçlara Dön' : 'View All Vehicles'}
          </Link>
        </div>
      </section>
    </div>
  );
};

// Vehicle Card Component
const VehicleCard = ({ vehicle, language, getLocalizedText, specLabels, getSpecLabel, getSpecIcon, t }) => {
  const name = getLocalizedText(vehicle, 'name');
  const description = getLocalizedText(vehicle, 'description');
  const mission = getLocalizedText(vehicle, 'mission');

  // Get top 4 specs to display
  const specs = vehicle.specs || {};
  const displaySpecs = Object.entries(specs)
    .filter(([key, value]) => value && key !== 'id')
    .slice(0, 4);

  return (
    <div 
      className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)',
        backdropFilter: 'blur(16px)'
      }}
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden" style={{ background: 'rgba(30, 41, 59, 0.4)' }}>
        <img 
          src={vehicle.image || 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=800'} 
          alt={name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center center' }}
          onError={(e) => {
            e.target.style.objectFit = 'contain';
            e.target.style.padding = '16px';
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, transparent 50%)'
          }}
        />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
          {mission && (
            <p className="text-cyan-400 text-sm">{mission}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {description && (
          <p className="text-gray-300 mb-6 line-clamp-2">{description}</p>
        )}

        {/* Quick Specs */}
        {displaySpecs.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            {displaySpecs.map(([key, value]) => {
              const Icon = getSpecIcon(key);
              return (
                <div 
                  key={key}
                  className="flex items-center gap-2 p-3 rounded-lg"
                  style={{ background: 'rgba(0, 240, 255, 0.05)' }}
                >
                  <Icon size={18} className="text-cyan-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 truncate">{getSpecLabel(key)}</p>
                    <p className="text-sm text-white font-medium truncate">{value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View Details Button */}
        <Link
          to={`/vehicle/${vehicle.id}`}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all"
          style={{
            background: 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)',
            color: '#0F172A'
          }}
        >
          {language === 'tr' ? 'Detayları Gör' : 'View Details'}
          <ChevronRight size={20} />
        </Link>
      </div>
    </div>
  );
};

export default VehicleCategory;
